import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Menu, LogOut, User, LayoutDashboard, Heart, MessageSquare, Shield, Bell, ChevronDown, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

const NAV = [
  { to: "/ilanlar", label: "İlanlar" },
  { to: "/kategoriler", label: "Kategoriler" },
  { to: "/toptan-firsatlar", label: "Toptan Fırsatlar" },
  { to: "/tarlada-kalanlar", label: "Tarlada Kalanlar" },
  { to: "/kurumsal-saticilar", label: "Kurumsal Satıcılar" },
  { to: "/alim-talepleri", label: "Alım Talepleri" },
];

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchCounts = () => {
      api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
      api.get("/messages/unread-count").then(({ data }) => setMsgUnread(data.count)).catch(() => {});
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/ilanlar?q=${encodeURIComponent(q)}`);
  };

  const displayName = user?.company?.company_name || user?.name;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 lg:px-6">
        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger"><Menu /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80">
            <div className="mt-2 flex flex-col gap-1">
              <Logo onClick={() => {}} />
              <nav className="mt-6 flex flex-col">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.to}>
                    <Link to={n.to} className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50">
                      {n.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        <Logo />

        {/* Search */}
        <form onSubmit={submitSearch} className="relative hidden flex-1 md:block max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ürün, kategori veya marka ara..."
            className="h-10 rounded-full border-slate-200 bg-slate-50 pl-10 focus-visible:ring-emerald-500"
          />
        </form>

        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <>
              <Link to="/panel/mesajlar" className="relative hidden sm:flex" data-testid="nav-messages">
                <Button variant="ghost" size="icon"><MessageSquare className="text-slate-600" /></Button>
                {msgUnread > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-bold text-white" data-testid="msg-unread-badge">
                    {msgUnread > 9 ? "9+" : msgUnread}
                  </span>
                )}
              </Link>
              <Link to="/panel" className="relative" data-testid="nav-notifications">
                <Button variant="ghost" size="icon">
                  <Bell className="text-slate-600" />
                  {unread > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1.5 rounded-full border-slate-200" data-testid="user-menu-trigger">
                    <User size={16} />
                    <span className="hidden max-w-[110px] truncate sm:inline">{displayName}</span>
                    <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{displayName}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/panel")} data-testid="menu-dashboard"><LayoutDashboard size={16} className="mr-2" />Panelim</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/panel/favoriler")}><Heart size={16} className="mr-2" />Favorilerim</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/panel/mesajlar")}><MessageSquare size={16} className="mr-2" />Mesajlar</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield size={16} className="mr-2" />Yönetim Paneli</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600"><LogOut size={16} className="mr-2" />Çıkış Yap</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/giris")} data-testid="header-login-btn" className="hidden sm:inline-flex">Giriş Yap</Button>
              <Button variant="outline" onClick={() => navigate("/kayit")} data-testid="header-register-btn" className="hidden sm:inline-flex border-slate-200">Üye Ol</Button>
            </>
          )}
          <Button
            onClick={() => navigate(user ? "/panel/yeni-ilan" : "/giris")}
            data-testid="header-create-listing-btn"
            className="gap-1.5 rounded-full bg-emerald-600 font-semibold hover:bg-emerald-700"
          >
            <Plus size={18} /> <span className="hidden sm:inline">İlan Ver</span>
          </Button>
        </div>
      </div>

      {/* desktop nav row */}
      <div className="hidden border-t border-slate-100 lg:block">
        <nav className="mx-auto flex max-w-7xl items-center gap-1 px-6">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className="px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:text-emerald-700"
            >
              {n.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Logo({ onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="logo-link" className="flex items-center gap-2">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-900 text-white">
        <Leaf size={20} className="text-emerald-400" />
      </span>
      <span className="font-display text-xl font-extrabold tracking-tight text-emerald-900">Çöpcepte</span>
    </Link>
  );
}
